import dataProvider from "src/dataProvider";
import { Project } from "src/types/api";

let fetcher = dataProvider("adminInstance");

const endPoints = {
  allProjects: "/projects?projectType=LAND_LEASED",
  myProfile: "/user",
  allUser: "/user",
  getProjectById: "/projects",
  getImageById: "/upload/file",
  updateProjectStatus: "/projects",
  allRegistrationDetails: "/projects/land-ownership",
};

export const getAllProjects = async (queryParams: any) => {
  return fetcher.get<{ count: number; rows: Project[] }>(
    `${endPoints.allProjects}`,
    queryParams
  );
};

export const getMyProfileData = async (id?: string) => {
  return fetcher.get(`${endPoints.myProfile}/${id}`);
};

export const updateUser = async (id: string, data: any) => {
  return fetcher.put(`${endPoints.myProfile}/${id}`, data);
};

/**
 * Corrects a State Nodal Officer's bank account only. Separate from updateUser
 * because PUT /user/:id rebuilds the whole profile from the body — sending a
 * partial payload there would blank the fields the admin did not resend.
 */
export const updateNodalOfficerBankDetail = async (
  id: string,
  data: {
    accountName?: string;
    bankName?: string;
    accountNumber?: string;
    ifscCode?: string;
  }
) => {
  return fetcher.patch(`${endPoints.myProfile}/${id}/bank-detail`, data);
};

export const getProjectById = async (id: string) => {
  return fetcher.get<Project>(`${endPoints.getProjectById}/${id}`);
};

export const getImageById = async (id: string) => {
  return fetcher.get(`${endPoints.getImageById}/${id}`);
};

export const updateProjectStatus = async (id: string, data: any) => {
  return fetcher.patch(`${endPoints.updateProjectStatus}/${id}`, data);
};

export const getAllUser = async (queryParams: any) => {
  return fetcher.get(`${endPoints.allUser}`, queryParams);
};

/**
 * id/name pairs only — for the "Filter By Entity" dropdowns. Unlike getAllUser
 * this returns the whole list in a few KB, so no limit/pagination is needed.
 */
export const getEntityOptions = async (queryParams?: {
  role?: string;
  status?: string;
  search?: string;
}) => {
  return fetcher.get<{ count: number; rows: { id: string; name: string }[] }>(
    `${endPoints.allUser}/options`,
    queryParams
  );
};

export const getAgencyCounts = async () => {
  return fetcher.get(`${endPoints.allUser}/agency-counts`);
};

export const getAllRegistration = async (queryParams: any) => {
  return fetcher.get<any>(`${endPoints.allRegistrationDetails}`, queryParams);
};

export type LandOwnershipEntityFilters = {
  status?: string;
  newSelection?: string | number;
  stateIds?: string;
  searchText?: string;
  dprStatus?: string;
  mouStatus?: string;
};

export type EntityOptionsSource = "landOwnership" | "progress" | "fund" | "payment";
const entityOptionsEndpoints: Record<EntityOptionsSource, string> = {
  landOwnership: `${endPoints.allRegistrationDetails}/entity-options`,
  progress: "/monitoring/target/land-ownerships/entity-options",
  fund: "/projects/land-ownership/fund-request/entity-options",
  payment: "/payments/entity-options",
};

export const getRecordEntityOptions = (
  source: EntityOptionsSource,
  filters: Record<string, any>
) => fetcher.get<{ count: number; rows: { id: string; name: string }[] }>(
  entityOptionsEndpoints[source], filters
);

export const demandDraft = (data: {
  id: string;
  data: {
    demandNote: number;
  };
}) => {
  return fetcher.patch<any>(
    `/projects/land-ownership/${data.id}/demand-notes`,
    data.data
  );
};

export const viewDemandDraft = (data: {
  id: string;
  data: {
    status: string;
    remark: string;
  };
}) => {
  return fetcher.patch<any>(
    `/projects/land-ownership/${data.id}/status`,
    data.data
  );
};

export const getLandDetailsById = async (id: string) => {
  return fetcher.get<any>(`/projects/land-ownership/${id}`);
};

export const userStatus = (data) => {
  return fetcher.patch<any>(`/user/status`, data);
};

export const verifyDemandDraft = (data: {
  id: string;
  data: {
    isVerified: boolean;
  };
}) => {
  return fetcher.patch<any>(
    `/projects/land-ownership/${data.id}/verify-demand-notes`,
    data.data
  );
};

export const verifyFundApplication = (data: {
  FundApplicationId: string;
  data: {
    status: string;
    remark: string;
    totalAmountReleased?: number;
    paymentApproveDocId?: string;
    releasedDate?: string;
  };
}) => {
  return fetcher.patch<any>(
    `/projects/land-ownership/fund-request/${data.FundApplicationId}/status`,
    data.data
  );
};

export const deleteUser = async (id: string) => {
  return fetcher.delete(`/user/${id}`);
};

export const queryHistory = (data) => {
  return fetcher.patch<any>(`/projects/remark/history`, data);
};

export const fundApplicationQuery = (data) => {
  return fetcher.post<any>(`/projects/land-ownership/fund-request/query`, data);
};

export const getDashboardDetails = async (queryParams: any) => {
  return fetcher.get<any>(`/projects/dashboard`, queryParams);
};

export const getLandDashboardDetails = async (queryParams: any) => {
  return fetcher.get<any>(`/projects/land-ownership/dashboard`, queryParams);
};

export const getUserDashboardDetails = async () => {
  return fetcher.get<any>(`/user/dashboard`);
};

export const getTableDashboardDetails = async (queryParams: any) => {
  return fetcher.get<any>(`/projects/dashboard/table`, queryParams);
};

export const getIOdDetails = async () => {
  return fetcher.get<any>(`/user/implementing-officers`);
};

export const addProjectToIo = async (
  projectId: string,
  implementingOfficerId: string
) => {
  return fetcher.patch(`/projects/${projectId}/implementing-officer`, {
    implementingOfficerId: implementingOfficerId,
  });
};

export const changePassword = async (data: any) => {
  return fetcher.post("/auth/change-password", data);
};

export const getEntityDashboardDetails = async (queryParams: any) => {
  return fetcher.get<any>(`/projects/dashboard/entity-table`, queryParams);
};

export type EntityCostSummary = {
  entityId: string;
  entityName: string;
  totalLandNumber: number;
  finalArea: number;
  demandNoteCost: number;
  ecoRestorationCost: number;
  totalReceived: number;
  releasedAmount: number;
};

export const getEntityCostSummary = () =>
  fetcher.get<{ rows: EntityCostSummary[] }>(
    `/projects/dashboard/entity-summary`
  );

export const getFundRequest = async (queryParams: any) => {
  return fetcher.get(`/projects/land-ownership/fund-request`, queryParams);
};

export const getFundDashboardStats = async (queryParams: {
  financialYear?: string;
  faYear?: number;
  stateId?: string;
}) => {
  return fetcher.get<any>(
    `/projects/land-ownership/fund-request/stats/dashboard`,
    queryParams
  );
};

export const getFundApplicationById = async (id: string) => {
  return fetcher.get<any>(`/projects/land-ownership/fund-request/${id}`);
};

export const updateFundRequestStatus = async (id: string, data: any) => {
  return fetcher.patch<any>(
    `/projects/land-ownership/fund-request/${id}/status`,
    data
  );
};

export const archiveFundRequest = async (id: string) => {
  return fetcher.delete(`/projects/land-ownership/fund-request/${id}`);
};

export const getArchivedFundRequests = async (queryParams: any) => {
  return fetcher.get(
    `/projects/land-ownership/fund-request/archive/list`,
    queryParams
  );
};

export const recoverFundRequest = async (id: string) => {
  return fetcher.patch(
    `/projects/land-ownership/fund-request/archive/${id}/recover`,
    {}
  );
};

export const getCostEstimateDetails = async (queryParams: any) => {
  return fetcher.get(`/monitoring/target`, queryParams);
};

export const getProgressDetails = async (queryParams: any) => {
  return fetcher.get(`/monitoring/target/land-ownerships`, queryParams);
};

export const getImages = async (id: any) => {
  return fetcher.get<any>(`/monitoring/progress/images`, {
    progressId: id,
  });
};

export const getProgressDetailsHistory = async (id: string) => {
  return fetcher.get<any>(`/monitoring/progress/history`, {
    targetId: id,
  });
};

export const getProgressReportDetails = async (queryParams: any) => {
  return fetcher.get<any>(`/monitoring/progress`, queryParams);
};

export const getMointoringMember = (id) => {
  return fetcher.get<any>(`/monitoring/committee?landOwnershipEpid=${id}`);
};

// Read-only: the admin portal never adds or edits committee members, it only
// reviews the trail left by the Implementing Officer / Entity.
export const getMointoringMemberHistory = (id) => {
  return fetcher.get<any>(`/monitoring/committee/member/${id}/history`);
};

export const getProgressYear = () => {
  return fetcher.get<any>(`/monitoring/target/year`);
};

export const getAllMonitoringDetailsData = async (queryParams: any) => {
  return fetcher.get<any>(`/monitoring`, queryParams);
};

export const getFinalSubmittedComponentDetails = async (epid: string) => {
  return fetcher.get<any>(`/monitoring/submitted/${epid}`);
};
export const getDemandNotePdf = async (epid: any) => {
  return fetcher.get<any>(`/projects/land-ownership/${epid}`, {
    download: true,
    origin: "https://admin.moefcc-gcpregistry.in/",
  });
};

export const getFundApplicationPdf = async (epid: any) => {
  return fetcher.get<any>(`/projects/land-ownership/fund-request/${epid}/pdf`);
};

export const putCostEstimateQueryDetails = async (data: any) => {
  return fetcher.put<any>(`/monitoring/target`, data);
};

export const updateSeedlingById = async (id: string, data: any) => {
  console.log(id, data, "data in api plantes seedling");
  return fetcher.put<any>(`/monitoring/target/seedling/${id}`, data);
};

export const withdrawProjectStatus = async (id: string, data: any) => {
  return fetcher.put<any>(`/projects/${id}/status`, data);
};

export const putDeselectDetails = async (epid: string, data: any) => {
  return fetcher.put<any>(`/projects/land-ownership/${epid}/withdraw`, data);
};

export const updateAcknowledgeByAdmin = async (epid: string, data: any) => {
  return fetcher.put<any>(`/projects/land-ownership/${epid}/acknowledge`, data);
};
export const updateAcknowledgeAdminRemark = async (epid: string, data: any) => {
  // console.log("epid", epid, data);
  return fetcher.put<any>(
    `/projects/land-ownership/${epid}/admin-selection`,
    data
  );
};
export const updateRejectByAdmin = async (epid: string, data: any) => {
  // console.log("epid", epid, data);
  return fetcher.put<any>(
    `/projects/land-ownership/${epid}/admin-deselection`,
    data
  );
};
export const submitAdminDoc = async (
  landOwnershipEpid: string,
  payload: any
) => {
  return fetcher.post(
    `/projects/land-ownership/${landOwnershipEpid}/document`,
    payload
  );
};

export const provisionallySelected = async (queryParams: any) => {
  return fetcher.get(`/projects/land-ownership`, queryParams);
};

export const getMonitoringLandOwnerships = (queryParams: any) => {
  return fetcher.get(`/projects/land-ownership`, queryParams);
};

// Target Query History APIs
export const getTargetQueries = async (targetId: string) => {
  return fetcher.get(`/monitoring/target/query/${targetId}`);
};

export const addTargetQuery = async (payload: {
  targetId: string;
  userId: string;
  query: string;
}) => {
  return fetcher.post(`/monitoring/target/query`, payload);
};

export const getSeedlingQueries = async (
  seedlingRecordId: string,
  page?: number,
  limit?: number
) => {
  return fetcher.get(`/monitoring/target/seedling-query/${seedlingRecordId}`, {
    page,
    limit,
  });
};

export const addSeedlingQuery = async (payload: {
  seedlingRecordId: string;
  userId: string;
  query: string;
}) => {
  return fetcher.post(`/monitoring/target/seedling-query`, payload);
};

// Monitoring Visit (new-monitoring) APIs
export const createMonitoringVisit = async (data: any) => {
  return fetcher.post<any>(`/new-monitoring`, data);
};

export const getMonitoringVisits = async (landOwnershipEpid: string) => {
  return fetcher.get<any>(`/new-monitoring`, { landOwnershipEpid });
};

// Monitoring Remarks APIs
export const createMonitoringRemark = async (data: {
  monitoringVisitId: string;
  remarks: string;
}) => {
  return fetcher.post<any>(`/new-monitoring/remarks`, data);
};

export const getMonitoringRemarks = async (monitoringVisitId: string) => {
  return fetcher.get<any>(`/new-monitoring/remarks`, { monitoringVisitId });
};

// Editing an Eco-Restoration Block from the admin activity details page.
// updateProjectDetails always sends editValue so the backend applies the edit
// in place instead of pushing the block back through verification.
export const updateProjectDetails = async (id: string, data: any) => {
  return fetcher.put<any>(`${endPoints.getProjectById}/${id}`, {
    ...data,
    editValue: true,
  });
};

export const addProjectDocument = async (
  projectId: string,
  data: { documentUrl: string; type: string }
) => {
  return fetcher.post<any>(
    `${endPoints.getProjectById}/${projectId}/document`,
    data
  );
};

export const deleteProjectDocument = async (
  projectId: string,
  documentId: string
) => {
  return fetcher.delete(
    `${endPoints.getProjectById}/${projectId}/document/${documentId}`
  );
};

export const getLandTypes = async () => {
  return fetcher.get<{ id: string; name: string }[]>(`/master/land-type`);
};

export const getDivisions = async (stateId?: string) => {
  return fetcher.get<any>(`/master/division`, stateId ? { stateId } : {});
};
